import { useEffect } from "react";
import { X, Minus, Plus, Trash2, ExternalLink, Loader2, ShoppingBag } from "lucide-react";
import { useCartStore } from "@/stores/useCart";
import { lockScroll, unlockScroll } from "@/lib/scroll-lock";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer = ({ isOpen, onClose }: CartDrawerProps) => {
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart } = useCartStore();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + parseFloat(item.price.amount) * item.quantity, 0);
  const currencyCode = items[0]?.price.currencyCode || "USD";

  useEffect(() => {
    if (isOpen) {
      syncCart();
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => { unlockScroll(); };
  }, [isOpen, syncCart]);

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      window.open(checkoutUrl, "_blank");
      onClose();
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-background/60 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 bottom-0 z-[70] w-full max-w-[420px] bg-background border-l border-border flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 border-b border-border" style={{ height: "var(--header-height)" }}>
          <h2 className="heading-small text-foreground">Cart ({totalItems})</h2>
          <button onClick={onClose} className="text-foreground hover:text-accent transition-colors" aria-label="Close cart">
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={40} strokeWidth={1} className="text-muted-foreground mb-4" />
              <p className="body-medium text-muted-foreground">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-5">
              {items.map((item) => (
                <div key={item.variantId} className="flex gap-4">
                  <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-muted">
                    {item.productImage && <img src={item.productImage} alt={item.productTitle} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="body-small text-foreground truncate">{item.productTitle}</p>
                    {item.variantTitle !== "Default Title" && (
                      <p className="body-small text-muted-foreground">{item.variantTitle}</p>
                    )}
                    <p className="body-small text-secondary-foreground mt-1">
                      {item.price.currencyCode} {parseFloat(item.price.amount).toFixed(0)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                        className="w-7 h-7 border border-border flex items-center justify-center hover:bg-muted transition-colors"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="body-small text-foreground w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                        className="w-7 h-7 border border-border flex items-center justify-center hover:bg-muted transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => removeItem(item.variantId)}
                        className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border px-6 py-5 space-y-4">
            <div className="flex justify-between items-center">
              <span className="body-medium text-foreground">Total</span>
              <span className="heading-small text-foreground">
                {currencyCode} {totalPrice.toFixed(0)}
              </span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={isLoading || isSyncing}
              className="btn w-full h-12 bg-accent text-accent-foreground hover:bg-accent-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading || isSyncing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <ExternalLink size={14} />
                  Checkout
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
